import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Users, Plus, X, Car } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { useConferma } from "./ConfirmProvider";
import { formatDate } from "../lib/format";

// 12/10/2026 — clienti collegati (familiari, lavoro, altro) nella scheda del cliente.
// Sara propone i collegamenti (stesso telefono o indirizzo); qui si vedono, si aggiungono
// a mano e si tolgono. Ogni collegato mostra le sue polizze attive.

const RELAZIONI = [
  { valore: "familiare", etichetta: "Familiare" },
  { valore: "lavoro", etichetta: "Lavoro" },
  { valore: "altro", etichetta: "Altro" },
];

export default function ClientiCollegati({ clientId }) {
  const navigate = useNavigate();
  const conferma = useConferma();
  const [collegati, setCollegati] = useState([]);
  const [aperto, setAperto] = useState(false);
  const [cerca, setCerca] = useState("");
  const [trovati, setTrovati] = useState([]);
  const [relazione, setRelazione] = useState("familiare");
  const [nota, setNota] = useState("");
  const [occupato, setOccupato] = useState(false);

  const carica = async () => {
    try {
      const r = await axios.get(`/api/clients/${clientId}/collegati`);
      setCollegati(r.data.collegati || []);
    } catch {
      setCollegati([]);
    }
  };

  useEffect(() => {
    carica();
    setAperto(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  useEffect(() => {
    if (!aperto || cerca.trim().length < 2) {
      setTrovati([]);
      return undefined;
    }
    const t = setTimeout(async () => {
      try {
        const r = await axios.get("/api/clients", { params: { search: cerca.trim(), limit: 8 } });
        const voci = Array.isArray(r.data) ? r.data : r.data.items || r.data.clients || [];
        const gia = new Set([clientId, ...collegati.map((c) => c.client_id)]);
        setTrovati(voci.filter((c) => !gia.has(c.client_id)));
      } catch {
        setTrovati([]);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [cerca, aperto, clientId, collegati]);

  const collega = async (altro) => {
    setOccupato(true);
    try {
      await axios.post(`/api/clients/${clientId}/collegati`, { client_id: altro.client_id, relazione, nota });
      toast.success(`Collegato a ${altro.name}`);
      setAperto(false);
      setCerca("");
      setNota("");
      await carica();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Collegamento non riuscito");
    } finally {
      setOccupato(false);
    }
  };

  const scollega = async (altro) => {
    if (!(await conferma({
      titolo: "Togliere il collegamento?",
      descrizione: `${altro.name} non comparirà più fra i collegati di questo cliente (e viceversa). I clienti non vengono toccati.`,
    }))) return;
    try {
      await axios.delete(`/api/clients/${clientId}/collegati/${altro.client_id}`);
      await carica();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Non riuscito");
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
          Familiari e collegati
        </CardTitle>
        <Button size="sm" variant="ghost" onClick={() => setAperto((v) => !v)} title="Collega un cliente">
          {aperto ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {aperto && (
          <div className="space-y-2 rounded-md border border-border/60 p-2">
            <Input autoFocus placeholder="Cerca il cliente da collegare…" value={cerca}
                   onChange={(e) => setCerca(e.target.value)} />
            <div className="flex flex-wrap gap-1">
              {RELAZIONI.map((r) => (
                <Button key={r.valore} size="sm" variant={relazione === r.valore ? "default" : "outline"}
                        onClick={() => setRelazione(r.valore)}>{r.etichetta}</Button>
              ))}
            </div>
            <Input placeholder="Nota (es. moglie, figlio, titolare)" value={nota}
                   onChange={(e) => setNota(e.target.value)} />
            {trovati.map((c) => (
              <button key={c.client_id} type="button" disabled={occupato}
                      className="w-full text-left text-sm rounded px-2 py-1 hover:bg-muted"
                      onClick={() => collega(c)}>
                {c.name}{c.city ? <span className="text-muted-foreground"> · {c.city}</span> : null}
              </button>
            ))}
          </div>
        )}

        {collegati.length === 0 && !aperto && (
          <p className="text-sm text-muted-foreground italic">Nessun cliente collegato</p>
        )}

        {collegati.map((c) => (
          <div key={c.client_id} className="space-y-1">
            <div className="flex items-center gap-2">
              <Users className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <button type="button" className="text-sm font-medium hover:underline text-left truncate"
                      onClick={() => navigate(`/clients/${c.client_id}`)}>
                {c.name}
              </button>
              <Badge variant="outline" className="text-xs shrink-0">{c.nota || c.relazione}</Badge>
              <Button size="sm" variant="ghost" className="ml-auto h-6 w-6 p-0" title="Togli il collegamento"
                      onClick={() => scollega(c)}>
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
            {(c.polizze || []).map((p) => (
              <button key={p.policy_id} type="button"
                      className="ml-5 flex items-center gap-1.5 text-xs text-muted-foreground hover:underline"
                      onClick={() => navigate(`/policies/${p.policy_id}`)}>
                <Car className="h-3 w-3" />
                {p.policy_type}{p.license_plate ? ` ${p.license_plate}` : ""} · {p.company} · scad. {formatDate(p.expiry_date)}
              </button>
            ))}
            {(c.polizze || []).length === 0 && (
              <p className="ml-5 text-xs text-muted-foreground italic">nessuna polizza attiva</p>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
